import { useEffect, useState } from "react";
import { api, SUB_ROLES, subRoleLabel } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, Check, X, ArrowRight } from "lucide-react";

export default function Partners() {
  const nav = useNavigate();
  const [filter, setFilter] = useState("all");
  const [browse, setBrowse] = useState([]);
  const [reqs, setReqs] = useState({ incoming: [], outgoing: [] });
  const [list, setList] = useState([]);

  const load = async () => {
    const q = filter && filter !== "all" ? `?sub_role=${filter}` : "";
    const [b, r, l] = await Promise.all([
      api.get(`/wholesalers/browse${q}`),
      api.get("/partners/requests"),
      api.get("/partners/list"),
    ]);
    setBrowse(b.data);
    setReqs(r.data);
    setList(l.data);
  };
  useEffect(() => { load(); }, [filter]);

  const sendReq = async (uid) => {
    try {
      await api.post("/partners/request", { to_user_id: uid, message: "" });
      toast.success("Partner request sent");
      load();
    } catch { toast.error("Failed"); }
  };

  const respond = async (id, accept) => {
    try {
      const { data } = await api.post(`/partners/respond/${id}?accept=${accept}`);
      toast.success(accept ? "Partnership formed" : "Declined");
      load();
      if (accept && data.partnership_id) nav(`/partners/${data.partnership_id}`);
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-8 md:p-10 max-w-[1400px]">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-widest text-[#39FF14] mb-2">// Accountability network</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight">
          Find your <span className="text-[#39FF14]">accountability partner</span>.
        </h1>
      </div>

      {/* Active partnerships */}
      {list.length > 0 && (
        <div className="mb-8">
          <h2 className="font-display text-sm font-bold uppercase tracking-widest text-neutral-400 mb-3">
            Your partners
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {list.map((p) => (
              <button
                key={p.partnership_id}
                data-testid={`open-partner-${p.partnership_id}`}
                onClick={() => nav(`/partners/${p.partnership_id}`)}
                className="hairline bg-[#0F0F0F] p-5 text-left hover:bg-[#141414] group transition-colors"
              >
                <div className="flex items-center gap-3 mb-3">
                  {p.partner?.picture ? (
                    <img src={p.partner.picture} className="w-11 h-11 hairline object-cover" alt="" />
                  ) : (
                    <div className="w-11 h-11 bg-[#1a1a1a] hairline flex items-center justify-center font-bold">
                      {p.partner?.name?.[0]}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{p.partner?.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                      {subRoleLabel(p.partner?.sub_role)}
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-neutral-500 group-hover:text-[#39FF14] transition-colors" />
                </div>
                {p.shared_goal && (
                  <div className="text-xs text-neutral-400 line-clamp-2">🎯 {p.shared_goal}</div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Incoming requests */}
      {reqs.incoming.length > 0 && (
        <div className="mb-8">
          <h2 className="font-display text-sm font-bold uppercase tracking-widest text-neutral-400 mb-3">
            Incoming requests
          </h2>
          <div className="space-y-2">
            {reqs.incoming.map((r) => (
              <div key={r.request_id} className="hairline bg-[#0F0F0F] p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-[#1a1a1a] hairline flex items-center justify-center font-bold">
                  {r.other_user?.name?.[0]}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-sm">{r.other_user?.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                    {subRoleLabel(r.other_user?.sub_role)}
                  </div>
                </div>
                <Button
                  data-testid={`accept-${r.request_id}`}
                  onClick={() => respond(r.request_id, true)}
                  className="rounded-none bg-[#39FF14] text-black hover:bg-white h-9 text-xs uppercase tracking-widest"
                >
                  <Check size={14} className="mr-1" /> Accept
                </Button>
                <Button
                  data-testid={`decline-${r.request_id}`}
                  onClick={() => respond(r.request_id, false)}
                  variant="outline"
                  className="rounded-none hairline bg-transparent h-9 text-xs uppercase tracking-widest"
                >
                  <X size={14} />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Browse */}
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-display text-sm font-bold uppercase tracking-widest text-neutral-400">
          Browse wholesalers
        </h2>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="filter-role" className="w-56 rounded-none bg-[#0F0F0F] hairline text-xs uppercase tracking-widest h-10">
            <SelectValue placeholder="Filter role" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-[#0F0F0F] hairline">
            <SelectItem value="all">All roles</SelectItem>
            {SUB_ROLES.map((r) => (
              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {browse.length === 0 && (
          <div className="col-span-full hairline bg-[#0F0F0F] p-10 text-center text-neutral-500 text-sm">
            No wholesalers found. Invite others to sign up.
          </div>
        )}
        {browse.map((u) => {
          const alreadyRequested = reqs.outgoing.some((o) => o.to_user_id === u.user_id);
          return (
            <div key={u.user_id} className="hairline bg-[#0F0F0F] p-5 hover:bg-[#141414] transition-colors">
              <div className="flex items-center gap-3 mb-3">
                {u.picture ? (
                  <img src={u.picture} className="w-11 h-11 hairline object-cover" alt="" />
                ) : (
                  <div className="w-11 h-11 bg-[#1a1a1a] hairline flex items-center justify-center font-bold">
                    {u.name[0]}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{u.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                    {subRoleLabel(u.sub_role)}
                  </div>
                </div>
              </div>
              {u.bio && <p className="text-xs text-neutral-400 line-clamp-2 mb-3">{u.bio}</p>}
              <Button
                data-testid={`request-${u.user_id}`}
                onClick={() => sendReq(u.user_id)}
                disabled={alreadyRequested}
                className="w-full rounded-none bg-[#39FF14] text-black hover:bg-white disabled:opacity-40 h-9 text-xs uppercase tracking-widest"
              >
                <UserPlus size={14} className="mr-2" />
                {alreadyRequested ? "Requested" : "Send request"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
